/**
 * Hook for fetching parent dashboard data.
 * Aggregates children, progress, and stats for the dashboard view.
 */

import { useState, useEffect, useCallback } from "react";
import { apiGet } from "../lib/api";
import type { AvatarId, DisciplineSlug, StarsByDiscipline } from "@jumpbean/shared";

// ----- Types -----

export interface ChildWithStats {
  id: string;
  displayName: string;
  avatarId: AvatarId;
  dateOfBirth: string | null;
  createdAt: string;
  updatedAt: string;
  stats?: {
    totalStars: number;
    totalBadges: number;
  };
}

export interface ChildrenResponse {
  children: ChildWithStats[];
  limit: number;
  canAddMore: boolean;
}

export interface ChildStatsResponse {
  childId: string;
  stats: {
    totalStars: number;
    totalBadges: number;
    currentStreakDays: number;
    longestStreakDays: number;
    lastPlayedAt: string | null;
    totalTimePlayedSeconds: number;
    starsByDiscipline: StarsByDiscipline;
    averageStarsPerChallenge: number;
  };
}

export interface DisciplineProgress {
  disciplineId: string;
  disciplineSlug: DisciplineSlug;
  disciplineName: string;
  totalChallenges: number;
  completedChallenges: number;
  percentComplete: number;
  totalStarsEarned: number;
  maxPossibleStars: number;
}

export interface ProgressResponse {
  childId: string;
  disciplines: DisciplineProgress[];
  summary: {
    totalStars: number;
    totalChallengesCompleted: number;
    totalChallenges: number;
    overallPercentComplete: number;
  };
}

export interface BadgeInfo {
  id: string;
  earnedAt: string;
  level: {
    id: string;
    number: number;
    name: string;
  } | null;
  badge: {
    name: string;
    imageUrl: string;
  } | null;
  discipline: {
    id: string;
    name: string;
    slug: DisciplineSlug;
  } | null;
}

export interface BadgesResponse {
  childId: string;
  badges: BadgeInfo[];
  totalBadges: number;
}

export interface DashboardChild {
  id: string;
  displayName: string;
  avatarId: AvatarId;
  stats: {
    totalStars: number;
    totalBadges: number;
    currentStreakDays: number;
    longestStreakDays: number;
    lastPlayedAt: string | null;
    totalTimePlayedSeconds: number;
    starsByDiscipline: StarsByDiscipline;
    averageStarsPerChallenge: number;
  };
  progress: {
    disciplines: DisciplineProgress[];
    overallPercentComplete: number;
  };
  recentBadges: BadgeInfo[];
}

export interface ParentDashboardData {
  children: DashboardChild[];
  canAddMore: boolean;
  childLimit: number;
  aggregateTotals: {
    totalStars: number;
    totalBadges: number;
    totalTimePlayedSeconds: number;
  };
}

// ----- Hook -----

export function useParentDashboard() {
  const [data, setData] = useState<ParentDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // First, get all children
      const childrenResult = await apiGet<ChildrenResponse>("/api/children");

      if (childrenResult.error) {
        throw new Error(childrenResult.error.message);
      }

      const childrenData = childrenResult.data;
      if (!childrenData) {
        throw new Error("Failed to fetch children");
      }

      // Fetch stats, progress, and badges for each child in parallel
      const childDataPromises = childrenData.children.map(async (child) => {
        const [statsResult, progressResult, badgesResult] = await Promise.all([
          apiGet<ChildStatsResponse>(`/api/progress/${child.id}/stats`),
          apiGet<ProgressResponse>(`/api/progress/${child.id}/progress`),
          apiGet<BadgesResponse>(`/api/progress/${child.id}/badges`),
        ]);

        const stats = statsResult.data?.stats ?? {
          totalStars: child.stats?.totalStars ?? 0,
          totalBadges: child.stats?.totalBadges ?? 0,
          currentStreakDays: 0,
          longestStreakDays: 0,
          lastPlayedAt: null,
          totalTimePlayedSeconds: 0,
          starsByDiscipline: { math: 0, spelling: 0, patterns: 0, typing: 0, programming: 0 },
          averageStarsPerChallenge: 0,
        };

        const progress = progressResult.data ?? {
          childId: child.id,
          disciplines: [],
          summary: { totalStars: 0, totalChallengesCompleted: 0, totalChallenges: 0, overallPercentComplete: 0 },
        };

        const badges = badgesResult.data?.badges ?? [];

        // Get most recent 3 badges
        const recentBadges = badges.slice(0, 3);

        return {
          id: child.id,
          displayName: child.displayName,
          avatarId: child.avatarId,
          stats,
          progress: {
            disciplines: progress.disciplines,
            overallPercentComplete: progress.summary.overallPercentComplete,
          },
          recentBadges,
        };
      });

      const dashboardChildren = await Promise.all(childDataPromises);

      // Calculate aggregate totals
      const aggregateTotals = dashboardChildren.reduce(
        (acc, child) => ({
          totalStars: acc.totalStars + child.stats.totalStars,
          totalBadges: acc.totalBadges + child.stats.totalBadges,
          totalTimePlayedSeconds: acc.totalTimePlayedSeconds + child.stats.totalTimePlayedSeconds,
        }),
        { totalStars: 0, totalBadges: 0, totalTimePlayedSeconds: 0 }
      );

      setData({
        children: dashboardChildren,
        canAddMore: childrenData.canAddMore,
        childLimit: childrenData.limit,
        aggregateTotals,
      });
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setError(err instanceof Error ? err.message : "Failed to load dashboard");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchDashboardData,
  };
}

export default useParentDashboard;
